import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Popper, Fade, Paper, useTheme, IconButton, alpha } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';

export interface PackageAiValidationAlertProps {
  validationState?: {
    loading?: boolean;
    valid?: boolean | null;
    suggestions?: string[];
    error?: string | null;
  };
}

export default function PackageAiValidationAlert({ validationState }: PackageAiValidationAlertProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isBadgeHovered, setIsBadgeHovered] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const lastStateRef = useRef<string>('');

  const loading = validationState?.loading;
  const valid = validationState?.valid;
  const suggestions = validationState?.suggestions || [];

  // Reset dismissal whenever validationState updates to a new result
  useEffect(() => {
    const currentStateSig = JSON.stringify({
      loading: validationState?.loading,
      valid: validationState?.valid,
      suggestions: validationState?.suggestions,
      error: validationState?.error,
    });

    if (currentStateSig !== lastStateRef.current) {
      lastStateRef.current = currentStateSig;
      setIsDismissed(false);
    }
  }, [validationState?.loading, validationState?.valid, validationState?.suggestions, validationState?.error]);

  // If valid === true or no validation has run yet, show nothing
  if (!loading && (valid === true || valid === null || valid === undefined)) {
    return null;
  }

  const isInvalid = !loading && valid === false;
  const isOpen = isInvalid && (!isDismissed || isBadgeHovered);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: 0.5 }}>
      {loading && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
            px: 1,
            py: 0.5,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '0.95rem' }} />
        </Box>
      )}

      {!loading && valid === false && (
        <>
          <Box
            ref={setAnchorEl}
            onMouseEnter={() => setIsBadgeHovered(true)}
            onMouseLeave={() => setIsBadgeHovered(false)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: theme.palette.error.main,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
              background: alpha(theme.palette.error.main, 0.05),
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: '0.95rem' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
              AI Alert{suggestions.length > 1 ? ` (${suggestions.length} issues)` : ''}
            </Typography>
          </Box>

          <Popper
            open={Boolean(isOpen && anchorEl)}
            anchorEl={anchorEl}
            placement="bottom-start"
            transition
            disablePortal={false}
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: true,
                options: { altAxis: true, padding: 12, boundary: 'clippingParents' },
              },
              {
                name: 'flip',
                enabled: true,
                options: { padding: 12 },
              },
              {
                name: 'offset',
                options: { offset: [0, 8] },
              },
            ]}
            style={{ zIndex: 990, maxWidth: 400 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={isDismissed ? 0 : { enter: 150, exit: 0 }}>
                <Paper
                  sx={{
                    mt: 1,
                    p: 1.75,
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                    border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
                    background: theme.palette.mode === 'dark' ? '#271717' : '#FFF8F8',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Top Header Row: Attention Required & Close Button on Top-Right */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          backgroundColor: alpha(theme.palette.error.main, 0.08),
                          px: '8px',
                          py: '2px',
                          borderRadius: '4px',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: theme.palette.error.main }}>
                          Attention Required
                        </Typography>
                      </Box>

                      <IconButton
                        size="small"
                        aria-label="Dismiss AI validation"
                        onClick={e => {
                          e.stopPropagation();
                          setIsBadgeHovered(false);
                          setIsDismissed(true);
                        }}
                        sx={{
                          p: '2px',
                          color: 'text.secondary',
                          opacity: 0.7,
                          '&:hover': { opacity: 1, backgroundColor: 'rgba(0, 0, 0, 0.06)' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: '0.85rem' }} />
                      </IconButton>
                    </Box>

                    {/* Content Row: Theme-Based Sparkle Icon + Validation Text & Bullets */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.25 }}>
                      <AutoAwesomeIcon
                        sx={{
                          fontSize: '1rem',
                          color: theme.palette.error.main,
                          mt: '2px',
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1 }}>
                        <Typography
                          sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}
                        >
                          {suggestions.length > 1
                            ? 'Package details need your attention.'
                            : suggestions.length === 1
                            ? suggestions[0]
                            : "This package couldn't be verified by AI."}
                        </Typography>

                        {suggestions.length > 1 && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.25 }}>
                            {suggestions.map((item, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Typography
                                  sx={{ fontSize: '0.8rem', color: theme.palette.error.main, lineHeight: 1.4 }}
                                >
                                  •
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}>
                                  {item}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Fade>
            )}
          </Popper>
        </>
      )}
    </Box>
  );
}
